import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import type {
  BreadcrumbList,
  ListItem,
  Person as PersonSchema,
  Review as ReviewType,
  Thing,
} from 'schema-dts';
import { env } from '@repo/env';
import type { Author, Reviewer } from '@repo/supergraph/base/types';
import type { BankingReview } from '@repo/supergraph/review/bankingPage/types';
import type { ReviewPage } from '@repo/supergraph/review/base/types';
import type { Roundup, RoundupCategory } from '@repo/supergraph/roundup/base/types';
import { linkedDataThumbnailUrls } from '@repo/supergraph/structuredContent/hydration/WpVideo/thumbnails';
import type { Video } from '@repo/supergraph/structuredContent/hydration/WpVideo/types';
import type {
  StructuredContent,
  StructuredContentDocument,
} from '@repo/supergraph/structuredContent/types';
import type { BreadcrumbRoot } from '../Breadcrumbs/types';
import type { BreadcrumbListSchema, ReviewProductSchema, VideoSchema } from '.';
import { NERDWALLET_AUTHOR_ID, ORGANIZATION } from './constants';

export const getAuthorSchema = (person: Author): PersonSchema => {
  const image = last(person.avatarUrls);
  return {
    '@type': 'Person',
    ...(Number(person.id) === NERDWALLET_AUTHOR_ID && { '@id': get(ORGANIZATION, '@id') }),
    ...(image && { image: image.url }),
    ...(person.name && { name: person.name }),
    ...(person.honorificSuffix && { honorificSuffix: person.honorificSuffix }),
    ...(person.jobTitle && { jobTitle: person.jobTitle }),
    ...(person.link && { url: person.link }),
    ...(person.description && { description: person.description }),
    ...(person.award && { award: person.award }),
    ...(person.affiliation && {
      affiliation: {
        '@type': 'Organization',
        name: person.affiliation,
      },
    }),
    ...(person.socialMediaLinks &&
      person.socialMediaLinks.length > 0 && {
        sameAs: person.socialMediaLinks.map(mediaLink => mediaLink.url),
      }),
    ...(person.alumniOf &&
      !Array.isArray(person.alumniOf) && {
        alumniOf: {
          '@type': 'EducationalOrganization',
          name: person.alumniOf,
        },
      }),
  };
};

export function getAuthorsSchema(
  post: ReviewPage | Roundup,
): PersonSchema | PersonSchema[] | undefined {
  if (!post.authors) return;

  if (post.authors.length > 1) {
    return post.authors.map((author: Author) => getAuthorSchema(author));
  }

  const firstAuthor = post.authors[0];
  if (!firstAuthor) return;

  return getAuthorSchema(firstAuthor);
}

export function getReviewerSchema(primaryReviewer: NonNullable<Reviewer>) {
  const image = primaryReviewer.featuredMedia?.croppedImage;

  return {
    '@type': 'Person',
    ...(image && { image: image.sourceUrl }),
    ...(primaryReviewer.name && { name: primaryReviewer.name }),
    ...(primaryReviewer.honorificSuffix && { honorificSuffix: primaryReviewer.honorificSuffix }),
    ...(primaryReviewer.jobTitle && { jobTitle: primaryReviewer.jobTitle }),
    ...(primaryReviewer.link && { url: primaryReviewer.link }),
    ...(primaryReviewer.alumniOf &&
      !Array.isArray(primaryReviewer.alumniOf) && {
        alumniOf: {
          '@type': 'EducationalOrganization',
          name: primaryReviewer.alumniOf,
        },
      }),
  };
}

export const extractStructuredContent = (
  structuredContent: StructuredContent | StructuredContent[],
): string | [string] => {
  if (!structuredContent || isEmpty(structuredContent)) {
    return '';
  }

  if (typeof structuredContent === 'string') {
    return structuredContent;
  }

  if (isArray(structuredContent)) {
    return structuredContent.map(content => extractStructuredContent(content)).join(' ');
  }

  if (typeof structuredContent === 'object') {
    return extractStructuredContent(get(structuredContent, 'props.children', ''));
  }

  return '';
};

export const getAboutSchema = (categories: RoundupCategory[]): Thing | Thing[] =>
  (categories ?? []).map(category => ({ name: category.name, '@type': 'Thing' }));

export const getBreadcrumbListSchema = (roots: BreadcrumbRoot[]): BreadcrumbListSchema => {
  const NW_URL = env.NEXT_PUBLIC_NW_URL;

  const listItems: BreadcrumbList['itemListElement'] = roots
    .map(({ page: { title: name }, slug: href }, idx) => {
      if (!href) return null;

      const isLastItem = idx === roots.length - 1;
      const itemUrl = new URL(href, NW_URL).toString();

      return {
        '@type': 'ListItem' as ListItem['@type'],
        position: idx + 2,
        name,
        item: isLastItem ? ({ '@id': itemUrl } as Thing) : itemUrl,
      };
    })
    .filter(item => !!item);

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: NW_URL,
      },
      ...listItems,
    ],
  };
};

export function getVideoSchema(video: Video): VideoSchema {
  const {
    youtubeVideo: { contentDetails, id, player, snippet },
  } = video;
  const { thumbnails, description, title, publishedAt } = snippet;
  const schema: VideoSchema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: title,
    description,
    thumbnailUrl: linkedDataThumbnailUrls(id, thumbnails),
    uploadDate: publishedAt,
    ...(contentDetails?.duration && { duration: contentDetails.duration }),
    ...(player?.embedHtml && { embedUrl: player.embedHtml }),
  };
  return schema;
}

export const parseCurrencyToNumber = (value: string | undefined): number | undefined => {
  if (!value) return undefined;
  const cleanValue = value.replace(/[$,]/g, '');
  const parsed = parseFloat(cleanValue);
  return isNaN(parsed) ? undefined : parsed;
};

const DEFAULT_IMAGE_URL = 'https://www.nerdwallet.com/assets/img/nw-logos/NW-default_og-image.jpg';

const getNestedReviewSchema = (
  review: BankingReview,
  reviewAuthor?: PersonSchema | PersonSchema[],
): ReviewType => {
  const reviewer = review?.reviewedBy || review?.internallyReviewedBy;

  const pros = review.pros.length > 0 ? review.pros : undefined;
  const cons = review.cons.length > 0 ? review.cons : undefined;

  return {
    '@type': 'Review',
    ...(review.authors && { author: reviewAuthor }),
    dateModified: review.modified,
    datePublished: review.displayDate,
    description: review.seo.description,
    headline: review.title,
    inLanguage: 'en',
    publisher: ORGANIZATION,
    url: review.link,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': review.link,
      ...(reviewer && { reviewedBy: getReviewerSchema(reviewer) }),
      url: review.link,
    },
    ...(pros && {
      positiveNotes: {
        '@type': 'ItemList',
        itemListElement: pros.map((pro: StructuredContentDocument, idx: number) => ({
          '@type': 'ListItem',
          position: idx,
          name: extractStructuredContent(pro),
        })),
      },
    }),
    ...(cons && {
      negativeNotes: {
        '@type': 'ItemList',
        itemListElement: cons.map((con: StructuredContentDocument, idx: number) => ({
          '@type': 'ListItem',
          position: idx,
          name: extractStructuredContent(con),
        })),
      },
    }),
    ...(review.marketplaceEntity?.starRating && {
      reviewRating: {
        '@type': 'Rating',
        worstRating: 0,
        bestRating: 5,
        ratingValue: review.marketplaceEntity.starRating,
      },
    }),
  };
};

export function getReviewProductSchema(review: ReviewPage): ReviewProductSchema {
  const reviewAuthor = getAuthorsSchema(review);
  const entity = review.marketplaceEntity;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: entity.displayName,
    brand: entity.displayName,
    image: entity.image?.source ?? DEFAULT_IMAGE_URL,
    identifier: entity.id,
    url: review.link,
    review: getNestedReviewSchema(review, reviewAuthor),
  };
}
